import { ItemsPerPage, useItemsPerPage } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'

import { InvoiceStatus } from '@features/invoices/enums/invoiceStatus'

import { PeriodMode } from '../../../../../enums/periodMode'
import { SortDirection } from '../../../../../enums/sortDirection'
import { useQueryParams } from '../../../../../hooks/useQueryParams'
import { ImportedInvoicesSortProperty } from '../../../enums/importedInvoicesSortProperty'

export interface ImportedInvoicesListFilters {
  page?: number
  pageSize?: ItemsPerPage
  period?: string
  searchQuery?: string
  sortDirection?: SortDirection
  sortProperty?: ImportedInvoicesSortProperty
  status?: InvoiceStatus
}

const defaultQuery: ImportedInvoicesListFilters = {
  page: 1,
  pageSize: undefined,
  // `encodePeriodValue` returns the bare mode for an all-time period, so the raw
  // string round-trips through `decodePeriodValue` and `usePeriodDates`.
  period: PeriodMode.All,
  searchQuery: '',
  sortDirection: SortDirection.Desc,
  // Must stay equal to the Date column's explicit `id` — the DS table compares the
  // two, so a mismatch drops the header chevron on first load.
  sortProperty: ImportedInvoicesSortProperty.UploadDate,
  status: InvoiceStatus.All,
}

type ContextState = [ImportedInvoicesListFilters, (filters: Partial<ImportedInvoicesListFilters>) => void]

const ImportedInvoicesListFiltersContext = createContext<ContextState | undefined>(undefined)

interface ImportedInvoicesListFiltersProviderProps {
  children?: ReactNode
}

export const ImportedInvoicesListFiltersProvider = ({
  children,
}: ImportedInvoicesListFiltersProviderProps): ReactElement => {
  const [pageSize] = useItemsPerPage()
  const [queryParams, setQueryParams] = useQueryParams<ImportedInvoicesListFilters>({
    enabled: true,
    defaultQuery: {
      ...defaultQuery,
      pageSize,
    },
  })

  const filters = useMemo(
    () => ({
      ...queryParams,
      page: Number(queryParams.page),
      pageSize: Number(queryParams.pageSize) as ItemsPerPage,
    }),
    [queryParams],
  )

  const setFilters = useCallback(
    (newFilters: Partial<ImportedInvoicesListFilters>) => {
      setQueryParams(newFilters)
    },
    [setQueryParams],
  )

  const value = useMemo((): ContextState => [filters, setFilters], [filters, setFilters])

  return (
    <ImportedInvoicesListFiltersContext.Provider value={value}>{children}</ImportedInvoicesListFiltersContext.Provider>
  )
}

export const useImportedInvoicesListFilters = (): ContextState => {
  const context = useContext(ImportedInvoicesListFiltersContext)

  if (!context) {
    throw new Error('ImportedInvoicesListFiltersContext is missing in the module!')
  }

  return context
}
